import { Injectable, inject } from '@angular/core';
import { Firestore, doc, collection, runTransaction, increment, docData, getDocs } from '@angular/fire/firestore';
import { Observable, of, from } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { Like, LikeTargetType } from '../models/like.model';

@Injectable({ providedIn: 'root' })
export class LikesService {
  private firestore = inject(Firestore);
  private auth = inject(AuthService);

  private getUid(): string | null { return this.auth.getCurrentUid(); }

  private likeDocId(type: LikeTargetType, targetId: string): string { return `${type}_${targetId}`; }

  private targetCollection(type: LikeTargetType): string {
    switch (type) {
      case 'model': return 'models';
      case 'image': return 'images';
      case 'video': return 'videos';
      default: return 'models';
    }
  }

  isLiked$(type: LikeTargetType, targetId: string): Observable<boolean> {
    return of(this.getUid()).pipe(
      switchMap(uid => {
        if (!uid) return of(false);
        const likeRef = doc(this.firestore, `users/${uid}/likes/${this.likeDocId(type, targetId)}`);
        return docData(likeRef).pipe(
          map(() => true),
          catchError(() => of(false))
        );
      })
    );
  }

  async toggleLike(type: LikeTargetType, targetId: string): Promise<void> {
    const uid = this.getUid();
    if (!uid) throw new Error('Not authenticated');

    const likeId = this.likeDocId(type, targetId);
    const likeRef = doc(this.firestore, `users/${uid}/likes/${likeId}`);
    const targetRef = doc(this.firestore, `${this.targetCollection(type)}/${targetId}`);

    await runTransaction(this.firestore, async (transaction) => {
      const likeSnap = await transaction.get(likeRef);
      const targetSnap = await transaction.get(targetRef);
      if (!targetSnap.exists()) throw new Error('Target does not exist');
      const currentCount = (targetSnap.data()['likesCount'] ?? 0) as number;

      if (likeSnap.exists()) {
        transaction.delete(likeRef);
        if (currentCount > 0) {
          transaction.update(targetRef, { likesCount: increment(-1) });
        } else {
          transaction.update(targetRef, { likesCount: 0 });
        }
      } else {
        const like: Like = {
          id: likeId,
            userId: uid,
          targetType: type,
          targetId: targetId,
          createdAt: new Date()
        };
        transaction.set(likeRef, like);
        transaction.update(targetRef, { likesCount: increment(1) });
      }
    });
  }

  getMyLikes$(): Observable<Like[]> {
    return of(this.getUid()).pipe(
      switchMap(uid => {
        if (!uid) return of([] as Like[]);
        const likesCol = collection(this.firestore, `users/${uid}/likes`);
        return from(getDocs(likesCol)).pipe(
          map((snapshot: any) => snapshot.docs.map((d: any) => ({ ...(d.data()), id: d.id }) as Like))
        );
      })
    );
  }
}
